import { ContractState, ContractViewModel, ServiceKind } from '@ail/contracts-api'
import { CustomerIndexViewModel } from '@ail/customers-api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, RouteComponentProps } from 'react-router-dom'
import { Arr } from '../../helpers/Arr'
import {
  formatNumberToLocale,
  formatNumberWithSpaces,
  getDeltaIconPerKind,
  printDate,
  roundUpToFixed,
} from '../../helpers/Utils'
import { useFilteredCustomers } from '../../hooks/useFilteredCustomers'
import { useSettings } from '../../hooks/useSettings'
import { useGetContractsBySecurityKeysQuery } from '../../redux/api/contractsApi'
import { useGetAllUnitsQuery } from '../../redux/api/customerApi'
import { useAppSelector } from '../../redux/store'
import { DeltaGridColEnum } from '../../types/enums'
import { Contracts } from '../../utils/contracts'
import DateSlider from '../common/DateSlider/DateSlider'
import { TextCell } from '../common/Grid/Cells/TextCell/TextCell'
import { Grid, GridColumn, InlineTextFilter, ModalContractsDropdownFilter } from '../common/Grid/Grid'
import './DeltaContracts.scss'
import GraphsJSX from './GraphsJSX'
import { useIsInterfleetPro } from '../../hooks/useIsInterfleetPro'
import { Loader } from '../common/Loader/Loader'

export interface ServiceKindHashMap {
  [serviceKind: string]: number | undefined
}

export interface GridRow {
  total: number
  totalActual: number
  totalExpected: number
  contract: ContractViewModel
  customer?: CustomerIndexViewModel
  expected: ServiceKindHashMap
  actual: ServiceKindHashMap
  serviceKindToDisplay: ServiceKindHashMap
  duration: string
  type: 'Difference' | 'Expected' | 'Actual' | 'Details'
}

type DeltaContractsFilters = {
  contract: InlineTextFilter<GridRow>['filterFn']
  costCenter: ModalContractsDropdownFilter<GridRow>['filterFn']
  deltaDriver: ModalContractsDropdownFilter<GridRow>['filterFn']
  plates: ModalContractsDropdownFilter<GridRow>['filterFn']
}

const INITIAL_DATES = {
  from: new Date(new Date().getFullYear() - 1, new Date().getMonth(), 1),
  to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
}

const graphKinds = [
  'KM',
  ServiceKind.Rmt,
  ServiceKind.Tires,
  ServiceKind.Fuel,
  ServiceKind.ReliefVehicle,
  ServiceKind.CarWash,
  ServiceKind.Vignette,
  ServiceKind.RoadTax,
  ServiceKind.Reestablishment,
  ServiceKind.Refurbishment,
  'TOT',
]

export default function DeltaContracts(props: RouteComponentProps<{ companyId: string }>) {
  const [selectedCustomerKeys, setSelectedCustomersKeys] = useState<string[] | undefined>([])
  const [sliderDateRange, setSliderDateRange] = useState<{ start: Date; end: Date }>()
  const [sliderMinMaxDates, setSliderMinMaxDates] = useState<{ start: Date; end: Date }>()
  const [sliderSelectedDates, setSliderSelectedDates] = useState<{ from: Date; to: Date }>(INITIAL_DATES)
  const [contractExpiration, setContractExpiration] = useState<string>('30')
  const [detailsVisibility, setDetailsVisibility] = useState<Record<string, number | undefined>>({})
  const user = useAppSelector((state) => state.appData.currentUser)
  const isInterfleetPro = useIsInterfleetPro()
  const customerIdFromUrl = props.match.params.companyId

  const [enableExtraGraphs, setEnableExtraGraphs] = useState(user?.isInternalUser || isInterfleetPro ? true : false)
  const [forceInternalView, setForceInternalView] = useState(false)
  // #region Custom Hooks

  const [, saveContractCostsInterval] = useSettings('contractCostsInterval', {
    remote: false,
  })

  const { t } = useTranslation('common')
  const allUnits = useGetAllUnitsQuery()
  const { customers: filteredCustomers } = useFilteredCustomers({
    status: 'ACTIVE',
  })

  const contracts = useGetContractsBySecurityKeysQuery(
    {
      securityKeys: selectedCustomerKeys!,
      detailFrom: sliderSelectedDates.from?.toDateString(),
      detailTo: sliderSelectedDates.to?.toDateString(),
    },
    { skip: !selectedCustomerKeys?.length }
  )
  // #endregion
  const filteredContracts = useMemo(() => {
    return contracts.currentData?.filter((c) => c.state === ContractState.Running || c.state === ContractState.Extended)
  }, [contracts.currentData])

  const limitDateRange = useCallback((startDate: Date, endDate: Date) => {
    function getYearDifference(date: Date) {
      const currentYear = new Date().getFullYear()
      return currentYear - date.getFullYear()
    }
    const now = new Date()
    let start = startDate
    let end = endDate
    if (getYearDifference(startDate) > 5) start = new Date(now.getFullYear() - 5, 0, 1)
    if (getYearDifference(endDate) < -1) end = new Date(now.getFullYear() + 1, 0, 1)
    return { start, end }
  }, [])

  const minMaxDatesRef = useRef<{ start: Date; end: Date }>()
  useEffect(() => {
    if (!!selectedCustomerKeys && !!filteredContracts) {
      let earliestStartDate = new Date()
      let latestEndDate = new Date()
      filteredContracts.forEach((c) => {
        const startDate = new Date(c.startDate).getTime()
        const endDate = new Date(c.endDate).getTime()

        if (startDate < earliestStartDate.getTime()) {
          earliestStartDate = new Date(startDate)
        }

        if (endDate > latestEndDate.getTime()) {
          latestEndDate = new Date(endDate)
        }
      })
      if (
        minMaxDatesRef.current &&
        minMaxDatesRef.current.start.getFullYear() === earliestStartDate.getFullYear() &&
        minMaxDatesRef.current.end.getFullYear() === latestEndDate.getFullYear()
      ) {
        return
      }

      setSliderMinMaxDates({ start: earliestStartDate, end: latestEndDate })
      setSliderDateRange(limitDateRange(earliestStartDate, latestEndDate))
      minMaxDatesRef.current = { start: earliestStartDate, end: latestEndDate }
    }
  }, [filteredContracts, limitDateRange, selectedCustomerKeys])

  useEffect(() => {
    if (!sliderDateRange || !sliderMinMaxDates) return

    if (sliderSelectedDates.from.getFullYear() === sliderDateRange.start.getFullYear()) {
      if (sliderDateRange.start.getFullYear() > sliderMinMaxDates.start.getFullYear()) {
        const start = new Date(sliderDateRange.start.getFullYear() - 1, 0, 1)
        setSliderDateRange({ start, end: sliderDateRange.end })
      }
    }
    if (sliderSelectedDates.to.getFullYear() === sliderDateRange.end.getFullYear()) {
      if (sliderDateRange.end.getFullYear() < sliderMinMaxDates.end.getFullYear()) {
        const end = new Date(sliderDateRange.end.getFullYear() + 1, 12, 0)
        setSliderDateRange({ start: sliderDateRange.start, end })
      }
    }
  }, [sliderDateRange, sliderMinMaxDates, sliderSelectedDates])

  // #region Data Maps
  const customerMap = useMemo(() => Arr.toHashMap(filteredCustomers ?? [], (c) => c.value!), [filteredCustomers])

  const unitsMap = useMemo(() => Arr.toHashMap(allUnits.data ?? [], (c) => c.value!), [allUnits.data])
  // #endregion

  useEffect(() => {
    if (customerIdFromUrl) {
      const customer = customerMap[`$${customerIdFromUrl.toUpperCase()}`]
      if (customer) setSelectedCustomersKeys([customer.value!])
    }
  }, [customerIdFromUrl, customerMap])

  // #region Callbacks
  const getCostCenterName = useCallback(
    (contract: ContractViewModel) => {
      if (unitsMap && contract.owner) {
        const relatedUnit = unitsMap[contract.owner]
        return relatedUnit && relatedUnit.unit ? relatedUnit.unit.name : t('NO_COST_CENTER')
      }
      return ''
    },
    [t, unitsMap]
  )

  const getCurrentDuration = useCallback((date: Date | string, duration?: number) => {
    const elapsed = getElapsedMonths(date)
    return duration ? `${Math.round(elapsed)} / ${duration}` : '-'
  }, [])

  const renderDetails = useCallback((row: GridRow) => {
    const actualRow: GridRow = {
      ...row,
      serviceKindToDisplay: row.actual,
      total: row.totalActual,
      type: 'Actual',
    }
    const expectedRow: GridRow = {
      ...row,
      serviceKindToDisplay: row.expected,
      total: row.totalExpected,
      type: 'Expected',
    }
    const detailsRow: GridRow = { ...row, type: 'Details' }
    return [expectedRow, actualRow, detailsRow]
  }, [])

  // #endregion
  // #region FiltersFNS
  const filterFns: DeltaContractsFilters = useMemo(() => {
    return {
      contract: (item, value) => !!item.contract.reference?.includes(value),
      costCenter: (item, value) => {
        const costCenter = getCostCenterName(item.contract)
        if (costCenter && value.includes(costCenter)) {
          return true
        }
        return false
      },
      plates: (item, value) => {
        const plate = item.contract.vehicle?.licensePlate?.licenseNumber
        if (plate && value.includes(plate)) {
          return true
        }
        return false
      },
      deltaDriver: (item, value) => {
        const driver = item.contract.vehicle?.driver?.displayName
        if (driver && value.includes(driver)) {
          return true
        }
        return false
      },
    }
  }, [getCostCenterName])
  // #region

  // #region dataRows
  const { dataRows, selectorOptionsList } = useMemo(() => {
    const data: GridRow[] = []
    const uniqueCostCenters = new Set<string>()
    const uniquePlates = new Set<string>()
    const uniqueDrivers = new Set<string>()

    let customer: CustomerIndexViewModel | undefined

    let allowedKinds = [
      ServiceKind.CarWash,
      ServiceKind.Fuel,
      ServiceKind.Vignette,
      ServiceKind.Tires,
      ServiceKind.RoadTax,
      ServiceKind.Rmt,
      ServiceKind.ReliefVehicle,
    ]
    if (enableExtraGraphs || isInterfleetPro) {
      allowedKinds = [...allowedKinds, ServiceKind.Reestablishment, ServiceKind.Refurbishment, ServiceKind.General]
    }

    filteredContracts?.forEach((contract) => {
      if (contract.owner) {
        const [customerKey] = contract.owner.split('//')
        if (customerKey) {
          customer = customerMap[customerKey]
        }
      }

      const actual: ServiceKindHashMap = {}
      const expected: ServiceKindHashMap = {}
      const serviceKindToDisplay: ServiceKindHashMap = {}
      let totalActual = 0
      let totalExpected = 0

      contract.monthlyCosts?.forEach((entry) => {
        if (!allowedKinds.includes(entry.serviceKind)) {
          return
        }
        const value = forceInternalView ? entry.internalAmount : entry.externalAmount
        const serviceKind = entry.serviceKind === ServiceKind.General ? ServiceKind.Reestablishment : entry.serviceKind
        if (actual[serviceKind]) {
          actual[serviceKind] = (actual[serviceKind] ?? 0) + (value ?? 0)
        } else {
          actual[serviceKind] = value ?? 0
        }
      })
      contract.monthlyRevenues?.forEach((entry) => {
        if (!allowedKinds.includes(entry.serviceKind)) return
        const serviceKind = entry.serviceKind === ServiceKind.General ? ServiceKind.Reestablishment : entry.serviceKind
        if (expected[serviceKind]) {
          expected[serviceKind] = (expected[serviceKind] ?? 0) + (entry.amount ?? 0)
        } else {
          expected[serviceKind] = entry.amount ?? 0
        }
      })

      const duration = getCurrentDuration(contract.startDate, contract.duration)
      for (const serviceKind of allowedKinds) {
        const actualValue = actual[serviceKind] ?? 0
        const expectedValue = expected[serviceKind] ?? 0
        actual[serviceKind] = actualValue
        expected[serviceKind] = expectedValue
        serviceKindToDisplay[serviceKind] = actualValue - expectedValue
        totalActual += actualValue
        totalExpected += expectedValue
      }
      const total = totalActual - totalExpected
      //skips adding a row with no costs data
      //! if we need to hide, comment it out
      // if (total === 0) return
      data.push({
        contract,
        customer,
        duration,
        actual,
        expected,
        totalActual,
        totalExpected,
        total,
        serviceKindToDisplay,
        type: 'Difference',
      })

      const costCenter = getCostCenterName(contract)
      if (costCenter) {
        uniqueCostCenters.add(costCenter)
      }
      const plate = contract.vehicle?.licensePlate?.licenseNumber
      if (plate) {
        uniquePlates.add(plate)
      }
      const driver = contract.vehicle?.driver?.displayName
      if (driver) {
        uniqueDrivers.add(driver)
      }
    })

    const selectorOptionsList = {
      costCenters: [...uniqueCostCenters].sort(),
      uniquePlates: [...uniquePlates].sort(),
      uniqueDrivers: [...uniqueDrivers].sort(),
    }
    return { dataRows: data, selectorOptionsList }
  }, [
    customerMap,
    filteredContracts,
    getCostCenterName,
    isInterfleetPro,
    enableExtraGraphs,
    forceInternalView,
    getCurrentDuration,
  ])
  // #endregion

  // #region Columns
  const columns = useMemo<GridColumn<GridRow>[]>(() => {
    const durations = ['30', '90', '180', '360', '450', '540', '720', '900', '1080', '1440']
    const getSelectorLabel = (incomingValue?: string) => {
      if (!incomingValue) return ''
      const val = Number(incomingValue)
      return val / 30 === 1 || val / 30 === 3 ? `${val} ${t('DAYS')}` : `${val / 30} ${t('MONTHS')}`
    }
    const getKMvalues = (row: GridRow, sort?: boolean) => {
      const monthDist = row.contract.monthlyDistances

      let actMileage = 0
      let expMileage = 0

      for (const dist of monthDist ?? []) {
        actMileage += dist.actualMonthDistance ?? 0
        expMileage += dist.expectedMonthDistance ?? 0
      }

      const diffMileage = actMileage - expMileage
      if (!sort) {
        if (row.type === 'Actual') return actMileage
        if (row.type === 'Expected') return expMileage
      }
      return diffMileage
    }
    const getColHeaderJSX = (title: string, icon: string | ServiceKind) => {
      const isServiceKind = Object.values(ServiceKind).includes(icon as ServiceKind)
      return (
        <>
          <i className={isServiceKind ? getDeltaIconPerKind(icon) : icon}></i>
          <span>{title}</span>
        </>
      )
    }

    const getFormattedValue = (key: ServiceKind, row: GridRow, dataType?: 'EXPECTED' | 'ACTUAL') => {
      const val = dataType
        ? dataType === 'ACTUAL'
          ? row.actual[key]
          : row.expected[key]
        : row.serviceKindToDisplay[key]
      return val ? formatNumberToLocale(val, 0, 0) : '0'
    }
    const getAdditionalExportColums = (key: ServiceKind, customLabel?: string) => {
      return [
        {
          label: `${t((customLabel ?? key).toUpperCase())} ${t('EXPECTED')}`,
          value: (row: GridRow) => getFormattedValue(key, row, 'EXPECTED'),
        },
        {
          label: `${t((customLabel ?? key).toUpperCase())} ${t('ACTUAL')}`,
          value: (row: GridRow) => getFormattedValue(key, row, 'ACTUAL'),
        },
      ]
    }
    return [
      {
        name: '',
        label: 'Cost Center',
        key: DeltaGridColEnum.customerName,
        width: 200,
        filter: {
          type: 'modal_delivery_customer_filter',
          data: filteredCustomers,
          format: (value) => value?.map((c) => customerMap[c]?.customer?.name ?? ''),
          onChange: (value) => {
            const validKeys = value?.filter((c) => customerMap[c])
            setSelectedCustomersKeys(validKeys?.length ? validKeys : undefined)
          },
          value: selectedCustomerKeys,
          autoTrigger: !selectedCustomerKeys || selectedCustomerKeys.length < 1 || !customerIdFromUrl,
          ignoreSaved: !!customerIdFromUrl,
          position: 'header-left',
          customPlaceholder: t('CHOOSE_CUSTOMER'),
        },
        renderCell: ({ row }) => {
          const { type, contract } = row
          const { vehicle } = contract
          const status = Contracts.getExtendedContractState(contract, Number(contractExpiration))

          if (type === 'Difference') {
            return (
              <>
                <NavLink
                  to={{
                    pathname: `/orderAndDelivery/details/${contract.id}`,
                  }}
                  onClick={() =>
                    saveContractCostsInterval({
                      from: sliderSelectedDates.from.toJSON(),
                      to: sliderSelectedDates.to.toJSON(),
                      contractId: contract.id,
                    })
                  }
                >
                  {contract.reference}
                </NavLink>
                <i
                  className="icon-info"
                  onClick={() => {
                    setDetailsVisibility((current) => {
                      return {
                        ...current,
                        [contract.id]: current[contract.id] ? undefined : 200,
                      }
                    })
                  }}
                />
                <span title={status} className={`statusArea ${status.toLowerCase()}`}>
                  {status.charAt(0).toUpperCase()}
                </span>
              </>
            )
          }

          if (type === 'Expected') {
            return (
              <div style={{ textAlign: 'right', width: '100%' }}>
                <span style={{ paddingRight: '15px' }}>{`${t('EXPECTED')}:`}</span>
              </div>
            )
          }

          if (type === 'Actual') {
            return (
              <div style={{ textAlign: 'right', width: '100%' }}>
                <span style={{ paddingRight: '15px' }}>{`${t('ACTUAL')}:`}</span>
              </div>
            )
          }

          if (type === 'Details') {
            return (
              <div className="infoRow">
                {contract.startDate && contract.endDate && (
                  <div className="infoRowItem">
                    <span className="infoRowTitle">{`${t('CONTRACT_DURATION')}: `}</span>
                    <span className="infoRowText">
                      {printDate(contract.startDate)} - {printDate(contract.endDate)}
                    </span>
                  </div>
                )}
                {vehicle?.licensePlate?.licenseNumber && (
                  <div className="infoRowItem">
                    <span className="infoRowTitle">{`${t('LICENSE_NUMBER')}: `}</span>
                    <span className="infoRowText">{vehicle.licensePlate.licenseNumber}</span>
                  </div>
                )}
                {vehicle?.driver?.displayName && (
                  <div className="infoRowItem">
                    <span className="infoRowTitle">{`${t('DRIVER_NAME')}: `}</span>
                    <span className="infoRowText">{vehicle.driver.displayName}</span>
                  </div>
                )}
                {vehicle?.description && (
                  <div className="infoRowItem">
                    <span className="infoRowTitle">{`${t('VEHICLE')}: `}</span>
                    <span className="infoRowText">{vehicle.description}</span>
                  </div>
                )}
              </div>
            )
          }
        },
        exportFormat: (row) => getCostCenterName(row.contract),
        //prettier-ignore
        additionalExportCols: [
          { label: t('CONTRACT'), value: (a) => a.contract.reference ?? t('NO_COST_CENTER')},
          { label: t('CONTRACT_DURATION'),value: (a) => `${a.contract.duration} ${t('MONTHS')} (${printDate(a.contract.startDate)} - ${printDate(a.contract.endDate)})`},
          { label: t('LICENSE_NUMBER'), value: (a) => a.contract.vehicle?.licensePlate?.licenseNumber },
          { label: t('DRIVER'), value: (a) => a.contract.vehicle?.driver?.displayName },
          { label: t('VEHICLE'), value: (a) => a.contract.vehicle?.description },
        ],
      },
      {
        name: getColHeaderJSX(t('MONTHS'), 'icon-calendar'),
        label: `${t('MONTHS')} Delta`,
        width: 80,
        key: DeltaGridColEnum.monthsDifForGrid,
        headerCellClass: 'delta-contracts-header new',
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('COST_CENTER_SEARCH'),
          filterFn: filterFns.costCenter,
          data: selectorOptionsList.costCenters,
          position: 'header-left',
          customPlaceholder: t('FILTER_COST_CENTER'),
        },
        renderCell: ({ row }) =>
          row.type !== 'Details' && <TextCell children={row.duration} style={{ textAlign: 'end', display: 'block' }} />,

        sortable: true,
        sortBy: (row) => row.duration,
        exportFormat: (row) =>
          (Number(row.contract.duration ?? 0) - Number(getElapsedMonths(row.contract.startDate))).toString(),
        additionalExportCols: [
          {
            label: `${t('MONTHS')} ${t('EXPECTED')}`,
            value: (row) => (row.contract.duration ? row.contract.duration.toString() : undefined),
          },
          {
            label: `${t('MONTHS')} ${t('ACTUAL')}`,
            value: (row) => getElapsedMonths(row.contract.startDate).toString(),
          },
        ],
      },
      {
        name: getColHeaderJSX(t('KM').toUpperCase(), 'icon-kilometers'),
        label: `${t('KM')} Delta`,
        width: 130,
        key: DeltaGridColEnum.km,
        headerCellClass: 'delta-contracts-header new',
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.contract,
          position: 'header-left',
          customPlaceholder: t('FILTER_CONTRACT_REF'),
        },
        renderCell: ({ row }) =>
          row.type !== 'Details' && (
            <TextCell
              children={formatNumberWithSpaces(getKMvalues(row))}
              style={{ textAlign: 'end', display: 'block' }}
            />
          ),
        sortable: true,
        sortBy: (row) => getKMvalues(row, true),
        exportFormat: (row) => formatNumberWithSpaces(getKMvalues(row)),
        additionalExportCols: [
          {
            label: `${t('KM')} ${t('EXPECTED')}`,
            value: (a) => formatNumberWithSpaces(getKMvalues({ ...a, type: 'Expected' })),
          },
          {
            label: `${t('KM')} ${t('ACTUAL')}`,
            value: (a) => formatNumberWithSpaces(getKMvalues({ ...a, type: 'Actual' })),
          },
        ],
      },
      {
        name: getColHeaderJSX(t('SRM'), ServiceKind.Rmt),
        label: `${t('SRM')} Delta`,
        width: 130,
        key: ServiceKind.Rmt,
        headerCellClass: 'delta-contracts-header new',
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('LICENCE_PLATE_SEARCH'),
          filterFn: filterFns.plates,
          data: selectorOptionsList.uniquePlates,
          position: 'header-left',
          customPlaceholder: t('FILTER_LICENCE_PLATE'),
        },
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            return (
              <TextCell
                children={getFormattedValue(ServiceKind.Rmt, row)}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.Rmt] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.Rmt, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.Rmt, 'SRM'),
      },
      {
        name: getColHeaderJSX(t('TIRES'), ServiceKind.Tires),
        label: `${t('TIRES')} Delta`,
        key: ServiceKind.Tires,
        headerCellClass: 'delta-contracts-header new',
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('DRIVER_SEARCH'),
          filterFn: filterFns.deltaDriver,
          data: selectorOptionsList.uniqueDrivers,
          position: 'header-left',
          customPlaceholder: t('FILTER_DRIVER'),
        },
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            return (
              <TextCell
                children={getFormattedValue(ServiceKind.Tires, row)}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.Tires] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.Tires, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.Tires),
      },
      {
        name: getColHeaderJSX(t('FUEL'), ServiceKind.Fuel),
        label: `${t('FUEL')} Delta`,
        key: ServiceKind.Fuel,
        headerCellClass: 'delta-contracts-header new',
        filter: {
          type: 'inline_selector_filter',
          data: durations,
          format: getSelectorLabel,
          position: 'header-right',
          onChange: (val) => val && setContractExpiration(val),
        },
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            return (
              <TextCell
                children={getFormattedValue(ServiceKind.Fuel, row)}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.Fuel] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.Fuel, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.Fuel),
      },
      {
        name: getColHeaderJSX(t('RELIEFVEHICLE'), ServiceKind.ReliefVehicle),
        label: `${t('RELIEFVEHICLE')} Delta`,
        width: 130,
        key: ServiceKind.ReliefVehicle,
        headerCellClass: 'delta-contracts-header new',
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            const val = row.serviceKindToDisplay[ServiceKind.ReliefVehicle]
            return (
              <TextCell
                children={val ? formatNumberToLocale(val, 0, 0) : 0}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.ReliefVehicle] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.ReliefVehicle, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.ReliefVehicle),
      },
      {
        name: getColHeaderJSX(t('CARWASH'), ServiceKind.CarWash),
        label: `${t('CARWASH')} Delta`,
        key: ServiceKind.CarWash,
        headerCellClass: 'delta-contracts-header new',
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            const val = row.serviceKindToDisplay[ServiceKind.CarWash]
            return (
              <TextCell
                children={val ? formatNumberToLocale(val, 0, 0) : 0}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.CarWash] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.CarWash, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.CarWash),
      },
      {
        name: getColHeaderJSX(t('VIGNETTE'), ServiceKind.Vignette),
        label: `${t('VIGNETTE')} Delta`,
        key: ServiceKind.Vignette,
        headerCellClass: 'delta-contracts-header new',
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            const val = row.serviceKindToDisplay[ServiceKind.Vignette]
            return (
              <TextCell
                children={val ? formatNumberToLocale(val, 0, 0) : 0}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.Vignette] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.Vignette, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.Vignette),
      },
      {
        name: getColHeaderJSX(t('ROADTAX'), ServiceKind.RoadTax),
        label: `${t('ROADTAX')} Delta`,
        key: ServiceKind.RoadTax,
        headerCellClass: 'delta-contracts-header new',
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            const val = row.serviceKindToDisplay[ServiceKind.RoadTax]
            return (
              <TextCell
                children={val ? formatNumberToLocale(val, 0, 0) : 0}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.RoadTax] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.RoadTax, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.RoadTax),
      },
      {
        name: getColHeaderJSX(t('REESTABLISHMENT'), ServiceKind.Reestablishment),
        label: `${t('REESTABLISHMENT')} Delta`,
        key: ServiceKind.Reestablishment,
        hide: !enableExtraGraphs,
        headerCellClass: 'delta-contracts-header new',
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            const val = row.serviceKindToDisplay[ServiceKind.Reestablishment]
            return (
              <TextCell
                children={val ? formatNumberToLocale(val, 0, 0) : 0}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.Reestablishment] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.Reestablishment, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.Reestablishment),
      },
      {
        name: getColHeaderJSX(t('REFURBISHMENT'), ServiceKind.Refurbishment),
        label: `${t('REFURBISHMENT')} Delta`,
        key: ServiceKind.Refurbishment,
        hide: !enableExtraGraphs,
        headerCellClass: 'delta-contracts-header new',
        renderCell: ({ row }) => {
          if (row.type !== 'Details') {
            const val = row.serviceKindToDisplay[ServiceKind.Refurbishment]
            return (
              <TextCell
                children={val ? formatNumberToLocale(val, 0, 0) : 0}
                style={{ textAlign: 'end', display: 'block' }}
              />
            )
          }
        },
        sortable: true,
        sortBy: (row) => row.serviceKindToDisplay[ServiceKind.Refurbishment] ?? 0,
        exportFormat: (row) => getFormattedValue(ServiceKind.Refurbishment, row),
        additionalExportCols: getAdditionalExportColums(ServiceKind.Refurbishment),
      },
      {
        name: <span>% {t('TOTAL')}</span>,
        label: `% ${t('TOTAL')}`,
        key: DeltaGridColEnum.totalPercent,
        headerCellClass: 'delta-contracts-header new',
        renderCell: ({ row }) => {
          if (row.type === 'Difference' && row.totalActual) {
            const val = ((row.totalActual - row.totalExpected) / row.totalExpected) * 100
            return (
              <span
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'right',
                }}
              >
                {`${roundUpToFixed(val)}%`}
              </span>
            )
          }
        },
        sortable: true,
        sortBy: (row) => (row.totalActual - row.totalExpected) / row.totalExpected,
        exportFormat: (row) => {
          return roundUpToFixed(((row.totalActual - row.totalExpected) / row.totalExpected) * 100).toString()
        },
      },
      {
        name: getColHeaderJSX(t('TOTAL'), 'icon-cost'),
        label: `${t('TOTAL')} Delta`,
        key: DeltaGridColEnum.total,
        headerCellClass: 'delta-contracts-header new',
        renderCell: ({ row }) => {
          if (row.type !== 'Details')
            return (
              <TextCell children={formatNumberToLocale(row.total, 0)} style={{ textAlign: 'end', display: 'block' }} />
            )
        },
        sortable: true,
        sortBy: (row) => row.total,
        exportFormat: (row) => row.total?.toFixed(2),
        additionalExportCols: [
          {
            label: `${t('TOTAL')} ${t('EXPECTED')}`,
            value: (a) => a.totalExpected.toFixed(2),
          },
          {
            label: `${t('TOTAL')} ${t('ACTUAL')}`,
            value: (a) => a.totalActual.toFixed(2),
          },
        ],
      },
    ]
  }, [
    filteredCustomers,
    selectedCustomerKeys,
    t,
    filterFns,
    selectorOptionsList,
    customerMap,
    contractExpiration,
    saveContractCostsInterval,
    sliderSelectedDates.from,
    sliderSelectedDates.to,
    enableExtraGraphs,
    customerIdFromUrl,
    getCostCenterName,
  ])
  // #endregion

  // #region Grid FX

  function getElapsedMonths(date: Date | string) {
    const currentDate = new Date()
    const startDate = typeof date === 'string' ? new Date(date) : date
    const elapsed = (currentDate.getTime() - startDate.getTime()) / 1000 / 3600 / 24 / 30
    return Math.round(elapsed)
  }
  const exportFileName = useMemo(() => {
    const base =
      selectedCustomerKeys
        ?.map((c) => {
          const name = customerMap[c]?.customer?.name ?? ''
          return name
            .replace(/,\s*/g, '')
            .replace(/[\s-]+/g, ' ')
            .trim()
            .replace(/[^a-zA-Z0-9]/g, '')
        })
        .filter((name) => name)
        .join('_')
        .slice(0, 30) || 'Unknown'
    return `Delta_${base}`
  }, [customerMap, selectedCustomerKeys])
  // #endregion

  return (
    <>
      <Loader blocking={contracts.isFetching} style={{ height: 'calc(100vh - 100px)' }}>
        <Grid
          id="Delta"
          columns={columns}
          rows={dataRows}
          rowKeyGetter={(item) => {
            return item.contract.id
          }}
          pagination={true}
          onReload={() => contracts.refetch()}
          renderSubHeader={(rows) => {
            return (
              <>
                {
                  <GraphsJSX
                    rows={rows}
                    kinds={graphKinds}
                    user={user}
                    isInternfleetPro
                    updateGraphVisibility={(val) => setEnableExtraGraphs(val)}
                  />
                }
                <div className="delta-date-slider-wrap-new">
                  <DateSlider
                    onUpdate={({ from, to }) =>
                      setSliderSelectedDates({
                        from,
                        to: new Date(to.getFullYear(), to.getMonth() + 1, 0),
                      })
                    }
                    startDate={sliderSelectedDates?.from}
                    endDate={sliderSelectedDates?.to}
                    includeLastYear={true}
                    minDateInSlider={sliderDateRange?.start}
                    maxDateInSLider={sliderDateRange?.end}
                    wholeRangeDrag={true}
                  />
                </div>
              </>
            )
          }}
          renderCustomOption={
            user?.isInternalUser
              ? () => (
                  <a className="grid-options-widget">
                    <i
                      style={{ padding: '5px' }}
                      className={forceInternalView ? 'icon-show' : 'icon-hide'}
                      onClick={() => setForceInternalView(!forceInternalView)}
                    ></i>
                  </a>
                )
              : undefined
          }
          detailsVisibility={detailsVisibility}
          renderDetails={(row) => renderDetails(row)}
          customClass="newDelta"
          hideFiltersBtn={true}
          hideReorder={true}
          headerHeight={55}
          showTotalItems={true}
          exportTitle={exportFileName}
        />
      </Loader>
    </>
  )
}
